import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/lib/auth";
import { useMedicationTemplatesStore } from "@/lib/stores/medicationTemplates";
import { useAdditionalNotesTemplatesStore } from "@/lib/stores/additionalNotesTemplates";
import { deleteMedicationTemplate } from "@/lib/stores/medicationTemplates";
import { deleteAdditionalNotesTemplate } from "@/lib/stores/additionalNotesTemplates";
import { SaveTemplateDialog } from "@/components/prescriptions/SaveTemplateDialog";
import { SaveAdditionalNotesTemplateDialog } from "@/components/prescriptions/SaveAdditionalNotesTemplateDialog";
import { getDoctorIdForUser, ensureDoctorProfile } from "@/lib/utils/doctorUtils";
import { Search, Trash2, Loader2, Plus, FileText, Pill, Eye, Edit } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Medication, MedicationTemplate, AdditionalNotesTemplate } from "@/types";
import { TemplateViewDialog } from "./TemplateViewDialog";

interface TemplatesDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TemplatesDialog({ isOpen, onClose }: TemplatesDialogProps) {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState("medication");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Medication templates
  const medicationTemplatesStore = useMedicationTemplatesStore();
  const [isLoadingMedication, setIsLoadingMedication] = useState(false);
  const [isDeletingMedication, setIsDeletingMedication] = useState<string | null>(null);
  const [showSaveMedicationDialog, setShowSaveMedicationDialog] = useState(false);
  const [medicationTemplateToSave, setMedicationTemplateToSave] = useState<Medication[]>([]);
  const [editingMedicationTemplate, setEditingMedicationTemplate] = useState<MedicationTemplate | null>(null);
  const [viewingTemplate, setViewingTemplate] = useState<MedicationTemplate | AdditionalNotesTemplate | null>(null);
  const [viewingTemplateType, setViewingTemplateType] = useState<"medication" | "additional-notes">("medication");
  
  // Additional notes templates
  const additionalNotesTemplatesStore = useAdditionalNotesTemplatesStore();
  const [isLoadingAdditionalNotes, setIsLoadingAdditionalNotes] = useState(false);
  const [isDeletingAdditionalNotes, setIsDeletingAdditionalNotes] = useState<string | null>(null);
  const [showSaveAdditionalNotesDialog, setShowSaveAdditionalNotesDialog] = useState(false);
  const [additionalNotesToSave, setAdditionalNotesToSave] = useState("");
  const [editingAdditionalNotesTemplate, setEditingAdditionalNotesTemplate] = useState<AdditionalNotesTemplate | null>(null);

  // Load templates when dialog opens
  useEffect(() => {
    if (isOpen && user?.id) {
      loadMedicationTemplates();
      loadAdditionalNotesTemplates();
    }
  }, [isOpen, user?.id]);

  // Clear search when tab changes
  useEffect(() => {
    setSearchTerm("");
  }, [activeTab]);

  const loadMedicationTemplates = async () => {
    if (!user?.id) return;
    
    setIsLoadingMedication(true);
    try {
      let doctorId = await getDoctorIdForUser(user);
      if (!doctorId) {
        doctorId = await ensureDoctorProfile(user);
        if (!doctorId) {
          toast({
            title: "Error",
            description: "Could not create doctor profile. Please contact administrator.",
            variant: "destructive"
          });
          return;
        }
      }
      await medicationTemplatesStore.fetchTemplates(doctorId);
    } catch (error) {
      console.error("Error loading medication templates:", error);
      toast({
        title: "Error loading templates",
        description: `Failed to load medication templates: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsLoadingMedication(false);
    }
  };

  const loadAdditionalNotesTemplates = async () => {
    if (!user?.id) return;
    
    setIsLoadingAdditionalNotes(true);
    try {
      let doctorId = await getDoctorIdForUser(user);
      if (!doctorId) {
        doctorId = await ensureDoctorProfile(user);
        if (!doctorId) {
          toast({
            title: "Error",
            description: "Could not create doctor profile. Please contact administrator.",
            variant: "destructive"
          });
          return;
        }
      }
      await additionalNotesTemplatesStore.fetchTemplates(doctorId);
    } catch (error) {
      console.error("Error loading additional notes templates:", error);
      toast({
        title: "Error loading templates",
        description: `Failed to load additional notes templates: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsLoadingAdditionalNotes(false);
    }
  };

  const handleDeleteMedicationTemplate = async (templateId: string) => {
    if (!confirm("Are you sure you want to delete this template?")) return;
    
    setIsDeletingMedication(templateId);
    try {
      await deleteMedicationTemplate(templateId);
      // Remove from store
      medicationTemplatesStore.removeTemplate(templateId);
      toast({
        title: "Template deleted",
        description: "The medication template has been deleted successfully.",
      });
      // Reload templates
      await loadMedicationTemplates();
    } catch (error) {
      console.error("Error deleting medication template:", error);
      toast({
        title: "Error deleting template",
        description: `Failed to delete template: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsDeletingMedication(null);
    }
  };

  const handleDeleteAdditionalNotesTemplate = async (templateId: string) => {
    if (!confirm("Are you sure you want to delete this template?")) return;
    
    setIsDeletingAdditionalNotes(templateId);
    try {
      await deleteAdditionalNotesTemplate(templateId);
      // Remove from store
      additionalNotesTemplatesStore.removeTemplate(templateId);
      toast({
        title: "Template deleted",
        description: "The additional notes template has been deleted successfully.",
      });
      // Reload templates
      await loadAdditionalNotesTemplates();
    } catch (error) {
      console.error("Error deleting additional notes template:", error);
      toast({
        title: "Error deleting template",
        description: `Failed to delete template: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsDeletingAdditionalNotes(null);
    }
  };

  const handleCreateMedicationTemplate = () => {
    setEditingMedicationTemplate(null);
    setMedicationTemplateToSave([]);
    setShowSaveMedicationDialog(true);
  };

  const handleCreateAdditionalNotesTemplate = () => {
    setEditingAdditionalNotesTemplate(null);
    setAdditionalNotesToSave("");
    setShowSaveAdditionalNotesDialog(true);
  };

  const handleViewMedicationTemplate = (template: MedicationTemplate) => {
    setViewingTemplate(template);
    setViewingTemplateType("medication");
  };

  const handleViewAdditionalNotesTemplate = (template: AdditionalNotesTemplate) => {
    setViewingTemplate(template);
    setViewingTemplateType("additional-notes");
  };

  const handleEditMedicationTemplate = (template: MedicationTemplate) => {
    setEditingMedicationTemplate(template);
    let medications: Medication[] = [];
    try {
      if (typeof template.medications === 'string') {
        medications = JSON.parse(template.medications);
      } else if (Array.isArray(template.medications)) {
        medications = template.medications;
      }
    } catch (e) {
      console.error('Error parsing medications:', e);
    }
    setMedicationTemplateToSave(medications);
    setShowSaveMedicationDialog(true);
  };

  const handleEditAdditionalNotesTemplate = (template: AdditionalNotesTemplate) => {
    setEditingAdditionalNotesTemplate(template);
    setAdditionalNotesToSave(template.notes || "");
    setShowSaveAdditionalNotesDialog(true);
  };

  const handleMedicationTemplateSaved = () => {
    setShowSaveMedicationDialog(false);
    setEditingMedicationTemplate(null);
    setMedicationTemplateToSave([]);
    loadMedicationTemplates();
  };

  const handleAdditionalNotesTemplateSaved = () => {
    setShowSaveAdditionalNotesDialog(false);
    setEditingAdditionalNotesTemplate(null);
    setAdditionalNotesToSave("");
    loadAdditionalNotesTemplates();
  };

  // Filter templates based on search term
  const filteredMedicationTemplates = searchTerm.trim()
    ? medicationTemplatesStore.templates.filter(template =>
        template.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.templateName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : medicationTemplatesStore.templates;

  const filteredAdditionalNotesTemplates = searchTerm.trim()
    ? additionalNotesTemplatesStore.templates.filter(template =>
        template.disease?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.templateName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : additionalNotesTemplatesStore.templates;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Management</DialogTitle>
            <DialogDescription>
              View and manage your medication and additional notes templates
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="medication" className="flex items-center gap-2">
                <Pill className="h-4 w-4" />
                Medication Templates
              </TabsTrigger>
              <TabsTrigger value="additional-notes" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Additional Notes Templates
              </TabsTrigger>
            </TabsList>

            {/* Medication Templates Tab */}
            <TabsContent value="medication" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search templates by diagnosis or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Button onClick={handleCreateMedicationTemplate} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create New
                </Button>
              </div>

              {isLoadingMedication ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading templates...</span>
                </div>
              ) : filteredMedicationTemplates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Pill className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No medication templates found</p>
                  <p className="text-sm mt-2">Create your first template to get started</p>
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Template Name</TableHead>
                        <TableHead>Diagnosis</TableHead>
                        <TableHead>Medications</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMedicationTemplates.map((template) => {
                        let medications: Medication[] = [];
                        try {
                          if (typeof template.medications === 'string') {
                            medications = JSON.parse(template.medications);
                          } else if (Array.isArray(template.medications)) {
                            medications = template.medications;
                          }
                        } catch (e) {
                          console.error('Error parsing medications:', e);
                        }

                        return (
                          <TableRow key={template.id}>
                            <TableCell className="font-medium">{template.templateName || 'Untitled'}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{template.diagnosis || 'N/A'}</Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {medications.length} medication(s)
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {template.createdAt
                                ? formatDistanceToNow(new Date(template.createdAt), { addSuffix: true })
                                : 'N/A'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewMedicationTemplate(template)}
                                  className="h-8 w-8 p-0"
                                  title="View details"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditMedicationTemplate(template)}
                                  className="h-8 w-8 p-0"
                                  title="Edit template"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteMedicationTemplate(template.id)}
                                  disabled={isDeletingMedication === template.id}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                  title="Delete template"
                                >
                                  {isDeletingMedication === template.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Additional Notes Templates Tab */}
            <TabsContent value="additional-notes" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search templates by disease or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Button onClick={handleCreateAdditionalNotesTemplate} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create New
                </Button>
              </div>

              {isLoadingAdditionalNotes ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading templates...</span>
                </div>
              ) : filteredAdditionalNotesTemplates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No additional notes templates found</p>
                  <p className="text-sm mt-2">Create your first template to get started</p>
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Template Name</TableHead>
                        <TableHead>Disease</TableHead>
                        <TableHead>Preview</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAdditionalNotesTemplates.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell className="font-medium">{template.templateName || 'Untitled'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{template.disease || 'N/A'}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <p className="text-sm text-muted-foreground truncate">
                              {template.notes || 'No preview available'}
                            </p>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {template.createdAt
                              ? formatDistanceToNow(new Date(template.createdAt), { addSuffix: true })
                              : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewAdditionalNotesTemplate(template)}
                                className="h-8 w-8 p-0"
                                title="View details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditAdditionalNotesTemplate(template)}
                                className="h-8 w-8 p-0"
                                title="Edit template"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAdditionalNotesTemplate(template.id)}
                                disabled={isDeletingAdditionalNotes === template.id}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                title="Delete template"
                              >
                                {isDeletingAdditionalNotes === template.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Save Medication Template Dialog */}
      <SaveTemplateDialog
        isOpen={showSaveMedicationDialog}
        onClose={() => {
          setShowSaveMedicationDialog(false);
          setEditingMedicationTemplate(null);
          setMedicationTemplateToSave([]);
        }}
        medications={medicationTemplateToSave}
        onTemplateSaved={handleMedicationTemplateSaved}
        allowEdit={true}
        editingTemplate={editingMedicationTemplate}
      />

      {/* Save Additional Notes Template Dialog */}
      <SaveAdditionalNotesTemplateDialog
        isOpen={showSaveAdditionalNotesDialog}
        onClose={() => {
          setShowSaveAdditionalNotesDialog(false);
          setEditingAdditionalNotesTemplate(null);
          setAdditionalNotesToSave("");
        }}
        notes={additionalNotesToSave}
        onTemplateSaved={handleAdditionalNotesTemplateSaved}
        editingTemplate={editingAdditionalNotesTemplate}
      />

      {/* View Template Details Dialog */}
      <TemplateViewDialog
        isOpen={viewingTemplate !== null}
        onClose={() => {
          setViewingTemplate(null);
          setViewingTemplateType("medication");
        }}
        template={viewingTemplate}
        type={viewingTemplateType}
      />
    </>
  );
}

